import React, { useState, useEffect } from 'react';
import { 
  Brain, Settings, FileText, BarChart3, TestTube2, BookOpen,
  AlertCircle, CheckCircle, Download, Upload
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import ModelsView from './ai-controls/ModelsView';
import PromptsView from './ai-controls/PromptsView';
import UsageView from './ai-controls/UsageView';
import TestConsoleView from './ai-controls/TestConsoleView';
import KnowledgeBaseView from './ai-controls/KnowledgeBaseView';

const AIControlsTab = () => {
  const [activeView, setActiveView] = useState('models');
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const { toast } = useToast();
  const API_BASE_URL = '/api';

  // Check if configs have been imported
  useEffect(() => {
    checkImportStatus();
  }, []);

  const checkImportStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/models?status=published`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setImported(data.data && data.data.length > 0);
      }
    } catch (error) {
      console.error('Error checking import status:', error);
    }
  };

  const handleImportConfigs = async () => {
    setImporting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/ai-controls/import-configs`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to import configs');

      const data = await response.json();
      
      toast({
        title: '✅ Import Successful',
        description: data.message,
        variant: 'default'
      });

      setImported(true);
    } catch (error) {
      toast({
        title: '❌ Import Failed',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
    }
  };

  const views = [
    { id: 'models', label: 'Models', icon: Brain, color: 'from-violet-400 to-purple-500' },
    { id: 'prompts', label: 'Prompts', icon: FileText, color: 'from-blue-400 to-cyan-500' },
    { id: 'usage', label: 'Usage & Cost', icon: BarChart3, color: 'from-green-400 to-emerald-500' },
    { id: 'test', label: 'Test Console', icon: TestTube2, color: 'from-orange-400 to-amber-500' },
    { id: 'knowledge', label: 'Knowledge Base', icon: BookOpen, color: 'from-pink-400 to-rose-500' }
  ];

  return (
    <div className="space-y-6">
      {/* Import Banner */}
      {!imported && (
        <Card className="p-6 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Initial Setup Required</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Import your existing AI configurations (models, prompts, brand voice) to get started.
                  This will create v1.0 baseline configs that you can manage and improve.
                </p>
                <Button
                  onClick={handleImportConfigs}
                  disabled={importing}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                >
                  {importing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Import Existing Configs
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Success Banner */}
      {imported && (
        <Card className="p-4 border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm font-medium text-gray-900">
              ✅ Configs imported successfully. Your AI features are now manageable through this panel.
            </p>
          </div>
        </Card>
      )}

      {/* Navigation Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {views.map(({ id, label, icon: Icon, color }) => (
          <button
            key={id}
            onClick={() => setActiveView(id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap ${
              activeView === id
                ? `bg-gradient-to-r ${color} text-white shadow-lg scale-105`
                : 'bg-white text-gray-600 hover:text-gray-900 hover:shadow-md border border-gray-200'
            }`}
          >
            <Icon className="w-5 h-5" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* View Content */}
      <div className="min-h-[600px]">
        {activeView === 'models' && <ModelsView />}
        {activeView === 'prompts' && <PromptsView />}
        {activeView === 'usage' && <UsageView />}
        {activeView === 'test' && <TestConsoleView />}
        {activeView === 'knowledge' && <KnowledgeBaseView />}
      </div>
    </div>
  );
};

export default AIControlsTab;